export interface BillingInformationForm {
  name: string
  org_name: string
  address_line_1: string
  city: string
  country_id: string
  state: string
  zipcode: string
  gst_number?: string
  gst_registered?: boolean
  actor: string
  client: string
  remarks: string
}

export interface UpdateDefaultCardPayload {
  card_mapping_id: number
}

export interface DeleteCardPayload {
  card_mapping_id: number
}

export interface RDeleteCard {
  success: boolean
  uuid: string
  message: string
}

export interface AddCardForm {
  addressline1: string
  cvc: string
  country: string
  number: string
  name: string
  year: string
  city: string
  zip: string
  state: string
  month: string
  setuprequired: boolean
  isdefault: boolean
  card_address?: boolean
}

export interface RAddCard {
  success: boolean
  uuid: string
  message: string
  redirect_url?: string
  error: string
  card_type: string
  card_name: string
  card_mapping_id?: string
  card_country: string
}

// =====================
// Stripe embedded Checkout (PSP-hosted card entry — PCI-safe)
// The raw PAN is entered inside Stripe's iframe and never touches our UI/servers.
// Flow: POST card/checkout-session -> mount initEmbeddedCheckout(client_secret)
// -> on complete, POST card/checkout-complete(checkout_session_id) -> card_mapping_id.
// =====================

// Request for POST /accounts/:account_id/card/checkout-session. `return_url` is only
// used if a card needs a redirect step to finish (rare); must be an https URL on our
// domain (not localhost/private IP).
export interface CardCheckoutSessionPayload {
  return_url: string
}

// Response from POST /accounts/:account_id/card/checkout-session. `client_secret` +
// `publishable_key` initialise Stripe.js embedded Checkout. An empty `publishable_key`
// means the backend has no Stripe config for this account's product/currency — surface
// an error and stop; Stripe.js cannot initialise.
export interface RCardCheckoutSession {
  success: boolean
  client_secret: string
  publishable_key: string
  checkout_session_id: string
  expires_at: string
  uuid: string
}

// Request for POST /accounts/:account_id/card/checkout-complete. `checkout_session_id`
// is returned by checkout-session (and also arrives as ?session_id= on the rare
// redirect-finish path).
export interface CardCheckoutCompletePayload {
  checkout_session_id: string
}

// Response from POST /accounts/:account_id/card/checkout-complete. `card_mapping_id` is
// the saved-card id used to charge later. Even if this call fails (e.g. browser closed),
// the Stripe webhook still persists the card moments later — treat a failure here as
// "verifying", the card will appear shortly.
export interface RCardCheckoutComplete {
  success: boolean
  message: string
  card_mapping_id: number
  card_country: string
  card_type: string
  card_name: string
  uuid: string
}

export interface UpdateBillingAlertsPayload {
  actor: string
  client: string
  send_email: boolean
  threshold_values: Array<{
    value: number
    status: boolean
  }>
}

export interface UpdateBillingEmailPayload {
  billing_email: string
}

export interface RBalanceAlerts {
  success: boolean
  uuid: string
  balance_threshold_details: Array<{
    account_id: number
    threshold_amount: number
    enabled: boolean
  }>
  daily_usage: number
}

export interface RUpdateDefaultCard {
  success: boolean
  uuid: string
  message: string
}

export interface RUpdateBillingInformation {
  success: boolean
  uuid: string
  account_billing: {
    id: number
    created_at: string
    updated_at: string
    account_id: number
    name: string
    address_line_1: string
    country_id: string
    state: string
    city: string
    zipcode: string
    sub_account_billing: boolean
    gst_number: string
  }
}

export interface AutoRechargeLimitForm {
  threshold_amount: number
  topup_amount: number
  auto_recharge: boolean
  meta: {
    source: string
    event: string
    message: string
  }
}

export interface RSetupAutoRecharge {
  success: boolean
  uuid: string
  message: string
}

export interface RechargePayload {
  account_id: number
  card_mapping_id: number
  amount: string
  // Target plan when this charge pays for a free-trial upgrade rather than a
  // top-up. Mirrors structs.RechargeRequest.UpgradePlan in payment-service, which
  // forwards it to TriggerV2Recharge and stamps it as Stripe charge metadata
  // (usecases/charge_card.go) for the webhook to act on. Set via
  // buildUpgradePlanPatch — never spell the literal at a call site.
  upgrade_plan?: string
}

export interface UpdateAuditLogsPayload {
  operation_type: 'auto_recharge_update'
  actor: string
  client: string
  prev_state: {
    auto_recharge: boolean
    recharge_below_amount: number
    recharge_amount: number
  }
  new_state: {
    auto_recharge: boolean
    recharge_below_amount: number
    recharge_amount: number
  }
  data: {
    account_id: string
    remarks: string
  }
  request_id: string
}

export interface RUpdateAuditLogs {
  success: boolean
  uuid: string
  message: string
}

export interface RRedirectionUrl {
  success: boolean
  uuid: string
  card_state: string
  error: string
  redirection_url: string
}

export enum CardState {
  ACTIVE = 'active',
  EXPIRED = 'expired',
  AUTHENTICATION_REQUIRED = 'authentication_required',
  DELETED = 'deleted',
  REMOVED = 'removed',
}

export type RiskStatus =
  | 'safe'
  | 'fraud'
  | 'blocked'
  | 'low_risk'
  | 'medium_risk'
  | 'high_risk'
  | 'cs_whitelisted'

export interface RAccountDetails {
  account: {
    account_product_type: string
    account_type: string
    address: string
    advance_payment: number
    auth_id: string
    auth_token: string
    auto_payment_approval: boolean
    billing_email: string
    billing_interval: number
    bulk_call_enabled: boolean
    card_verification_needed: boolean
    carrier_type: string
    city: string
    comments: string
    company: string
    conversion_rate: number
    country_id: string
    created: string
    credit_limit: number
    currency: string
    custom_subdomain: boolean
    customer_type: string
    email: string
    enabled: boolean
    fax_allowed: boolean
    features: string
    features_callstats_enabled: boolean
    features_callstats_key: string
    features_parent_values: string
    features_values: string
    id: number
    inbound_billing_interval: string
    invite_details: unknown[]
    invoice_data: string
    is_staff: boolean
    is_user_invited: boolean
    mps_allowed: number
    name: string
    payment_period: number
    // Plivo product plan. Projected by hodor's billing response filter, which
    // yields null for plan_name when account-service omits it.
    plan_id: number | null
    plan_name: string | null
    plivo_carrier_fallback: boolean
    postpaid: boolean
    proof_verified: boolean
    registered_ip: string
    risk_rating: number
    risk_status: RiskStatus
    sender_id_whitelisted: boolean
    show_billing_data: boolean
    sms_carrier_profile_group_id: number
    sms_cost_check: boolean
    sms_routing_strategy: string
    state: string
    timezone: string
    user_id: number
    username: string
    zip_code: string
  }
}

export interface VerifyGSTNumberPayload {
  task_id: string
  group_id: string
  data: {
    gstin: string
    filling_status: boolean
  }
}

export interface RVerifyGSTNumber {
  task_id: string
  group_id: string
  status: string
  result: {
    source_output: {
      gstin_status: string
      legal_name: string
      trade_name: string
    }
  }
}
export interface SendBlockedEmailPayload {
  email_list: string[]
}

export interface VerifyZipCodePayload {
  zipcode: string
}

export interface RVerifyZipCode {
  Message: string
  Status: string
  PostOffice: Array<{
    Name: string
    Description: string | null
    BranchType: string
    DeliveryStatus: string
    Circle: string
    District: string
    Division: string
    Region: string
    Block: string
    State: string
    Country: string
    Pincode: string
  }>
}

export interface ConversionRate {
  conversionRate: number
  currencyCode: string
  isLoading: boolean
}

// ============================================
// Bank Direct Debit (ACH) Types
// ============================================

export enum BankAccountState {
  VERIFICATION_REQUIRED = 'verification_required',
  ACTIVE = 'active',
  BLOCKED = 'blocked',
}

export type BankAccountType = 'checking' | 'savings'
export type AccountHolderType = 'individual' | 'company'

export interface BankAccount {
  id: number
  account_id: number
  last_four_digits: string
  bank_code: string
  alias_name: string
  account_type: BankAccountType
  account_holder_type: AccountHolderType
  state: BankAccountState
  created_at: string
  updated_at?: string
}

export interface AddBankAccountForm {
  account_type: BankAccountType
  account_holder_type: AccountHolderType
  name: string
  alias_name: string
  account_number: string
  routing_number: string
}

export interface AddBankAccountPayload {
  account_id: number
  email: string
  name: string
  account_number: string
  routing_number: string
  alias_name: string
  account_type: BankAccountType
  account_holder_type: AccountHolderType
  ip_address: string
  user_agent: string
}

export interface DeleteBankAccountPayload {
  account_id: number
  bank_mapping_id: number
  client: string
  actor: string
}

export interface VerifyBankAccountPayload {
  account_id: number
  bank_mapping_id: number
  descriptor_code: string
  client: string
  actor: string
}

export interface RBankAccountList {
  account_bank_mappings: BankAccount[]
  total_count: number
}

export interface RAddBankAccount {
  success: boolean
  customer_message: string
  id: number
}

export interface RDeleteBankAccount {
  success: boolean
  message: string
}

export interface RVerifyBankAccount {
  success: boolean
  message: string
}

// Auto Debit Schedule Types
export interface AutoDebitSchedule {
  id: string
  bank_mapping_id: number
  amount: number
  charge_day_of_month: number
  skip_date: string | null
  created_at: string
  updated_at: string
}

export interface AddSchedulePayload {
  account_id: number
  bank_mapping_id: number
  amount: number
  charge_day_of_month: number
  actor: string
  client: string
}

export interface UpdateSchedulePayload {
  account_id: number
  bank_mapping_id: number
  amount: number
  charge_day_of_month: number
  skipped?: boolean
  actor: string
  client: string
}

export interface RAutoDebitScheduleList {
  objects: AutoDebitSchedule[]
  total_count: number
}

export interface RAddSchedule {
  success: boolean
  id: string
  bank_mapping_id: number
  amount: number
  charge_day_of_month: number
  message: string
}

export interface RUpdateSchedule {
  success: boolean
  id: string
  bank_mapping_id: number
  amount: number
  charge_day_of_month: number
  skip_date: string | null
  message: string
}

export interface RDeleteSchedule {
  success: boolean
  message: string
}

// ACH Status Types
export interface RACHStatus {
  enabled: boolean
}

export interface ToggleACHPayload {
  enabled: boolean
  bank_mapping_id: number
  actor: string
  client: string
}

export interface RToggleACH {
  success: boolean
  enabled: boolean
  message: string
}

// Virtual Bank Details (for USD Bank Transfer)
// Matches the `aba` object inside `financial_addresses` from wire_transfer/funding_instructions API
export interface VirtualBankDetails {
  account_holder_name: string
  bank_name: string
  account_number: string
  account_type: string
  routing_number: string
  bank_address: {
    line1: string
    line2: string
    city: string
    state: string
    postal_code: string
    country: string
  }
}

export interface FinancialAddress {
  type: string
  aba: VirtualBankDetails
}

export interface RVirtualBankDetails {
  success: boolean
  bank_details: {
    bank_transfer: {
      financial_addresses: FinancialAddress[]
    }
  }
}

// =====================
// Cashfree (India PSP)
// =====================

export interface CashfreeCard {
  instrument_id: string
  card_network: string
  card_type: string
  card_bank_name: string
  card_country: string
  last_4: string
  status: string
  added_at: string
}

export interface RCashfreeCards {
  success: boolean
  uuid: string
  cards: CashfreeCard[]
}

// Request payload for `POST /in/order/create`. Only `card` and `netbanking`
// are wired up; UPI is intentionally excluded.
export interface CashfreeOrderCreatePayload {
  account_id: number
  amount: number
  product_type: 'plivo'
  payment_method_type: 'card' | 'netbanking'
  return_url: string
  // Target plan when this order pays for a plan upgrade rather than a top-up.
  upgrade_plan?: string
}

// Response from `POST /in/order/create`. `payment_session_id` is fed into the
// Cashfree JS SDK (`cashfree.checkout({ paymentSessionId })`) to launch the
// hosted checkout. This is the PG Orders flow — distinct from
// `/in/payment/create` which returns a `link_url` for the legacy Payment Link
// redirect.
export interface RCashfreeOrder {
  success: boolean
  message: string
  uuid: string
  order_id: string
  cf_order_id: string
  payment_session_id: string
  amount: number
  gateway_fee: number
  total_amount: number
  currency: string
  status: string
  expires_at: string
}

// Cashfree S2S Pay (POST /in/pay). Send `card`+`save_card` OR `card_mapping_id`
// — backend rejects both. customer_phone seeds the Cashfree customer on first use.
export interface CashfreeNewCardDetails {
  card_number: string
  card_holder_name: string
  card_expiry_mm: string
  card_expiry_yy: string
  card_cvv: string
}

export interface CashfreePayPayload {
  account_id: number
  amount: number
  product_type: 'plivo'
  customer_phone?: string
  return_url?: string
  save_card?: boolean
  card?: CashfreeNewCardDetails
  card_mapping_id?: number
  cvv?: string
}

// `payment_url` is the 3DS redirect target — caller does window.location.href.
export interface RCashfreePay {
  success: boolean
  message: string
  uuid?: string
  order_id: string
  cf_order_id: string
  cf_payment_id?: number
  action?: string
  payment_url?: string
  amount: number
  gateway_fee?: number
  total_amount?: number
  currency: string
  status: string
}

// Response from `POST /in/payment/:order_id/verify` and `GET /in/payment/:order_id`.
// The two endpoints surface `payment_status` from different sources, so the
// value vocabularies differ:
//   - verify (POST) returns Cashfree's raw OrderStatus: PAID / ACTIVE /
//     EXPIRED / TERMINATED / PARTIALLY_PAID.
//   - get-status (GET) returns the DB-mapped status: SUCCESS / PENDING /
//     FAILED / USER_DROPPED / CANCELLED / VOID / NOT_ATTEMPTED.
// Callers must handle both vocabularies (see payment-callback.tsx).
export interface CashfreePaymentStatus {
  success: boolean
  order_id: string
  cf_order_id: string
  payment_status: string
  amount: number
  currency: string
  cf_payment_id?: number
  bank_reference?: string
  payment_time?: string
}

// ── Razorpay (India PSP) ─────────────────────────────────────────────────
// Plan-B direct routes: these map to payment-service PR #382 /v2/razorpay/*.
// When the backend routes the PSP-agnostic /in/* alias to Razorpay, only the
// endpoint strings in billing-service.ts change — these shapes stay the same.

// Request for POST /razorpay/orders. save_card provisions a Razorpay customer
// and returns razorpay_customer_id so the hosted modal can vault the card.
export interface RazorpayOrderCreatePayload {
  account_id: number
  amount: number
  currency?: 'INR'
  product_type: 'plivo'
  // Restricts Razorpay's hosted modal to one method. Backend accepts
  // card / netbanking / upi; the console offers the first two.
  payment_method?: 'card' | 'netbanking'
  save_card?: boolean
  customer_name?: string
  customer_email?: string
  customer_phone?: string
  upgrade_plan?: string
}

// Response from POST /razorpay/orders. razorpay_order_id + key_id + amount_paise
// are handed to the Razorpay checkout SDK to launch the hosted card-only modal.
export interface RRazorpayOrder {
  success: boolean
  uuid?: string
  razorpay_order_id: string
  razorpay_customer_id?: string
  key_id: string
  amount: number
  amount_paise: number
  currency: string
  status: string
}

// Request for POST /razorpay/payment/verify — the signature triple razorpay.js
// returns to the checkout handler on success.
export interface RazorpayVerifyPayload {
  razorpay_order_id: string
  razorpay_payment_id: string
  razorpay_signature: string
}

export interface RRazorpayVerify {
  success: boolean
  verified: boolean
  payment_id: string
  order_id: string
  status?: string
  method?: string
  amount?: number
  currency?: string
  captured?: boolean
}

// Request for POST /v2/razorpay/pay-with-token — one-tap charge of a saved
// (tokenised) card via Razorpay's Native/S2S JSON API, customer present.
// Mirrors usecases.RazorpayPayWithTokenRequest in payment-service (verified
// field-by-field against that source, including amount units).
//
// The card is identified by card_mapping_id, NOT by a Razorpay token: the
// backend resolves the mapping → customer card → token itself, and rejects a
// card that isn't RAZORPAY-issued or isn't active. `amount` is in RUPEES — the
// backend adds the gateway fee and converts to paise (chargeCtx.totalChargePaise),
// so send the same figure the amount tiles show.
export interface RazorpayPayWithTokenPayload {
  account_id: number
  amount: number
  card_mapping_id: number
  // NOTE the backend also accepts an optional `cvv` (3-4 digits) that it forwards
  // to Razorpay. The console does not collect one for a tokenised charge, so it is
  // omitted here — add it back to this type if that changes.
  product_type?: 'plivo'
  description?: string
  // Where Razorpay sends the customer after the OTP page. Omit to use the URL
  // configured on the Razorpay dashboard — Razorpay POSTs the result back, which
  // a static SPA route cannot read, so the dashboard URL (a backend endpoint) is
  // the correct target.
  callback_url?: string
  upgrade_plan?: string
  notes?: Record<string, string>
}

// Response from GET /v2/razorpay/payments/:payment_id
// (usecases.RazorpayPaymentStatusResponse). `status` is Razorpay's own payment
// lifecycle value — created / authorized / captured / refunded / failed — read
// live via FetchPayment, NOT our payment_log mirror. That is why it can answer a
// 3DS return immediately, while the balance still waits on the webhook.
export interface RRazorpayPaymentStatus {
  success: boolean
  message?: string
  payment_id: string
  order_id: string
  status: string
  amount: number
  currency: string
  method: string
  captured: boolean
}

// Response from /v2/razorpay/pay-with-token (usecases.RazorpayPayWithTokenResponse).
// NOTE there is no `captured` flag and no verify step: a successful response only
// means the charge was SUBMITTED. When requires_action is true the customer must
// be sent to next_action_url (Razorpay-hosted bank OTP). Terminal credit/failure
// arrives via the existing payment.captured / payment.failed webhooks.
export interface RRazorpayPayWithToken {
  success: boolean
  message?: string
  error?: string
  uuid?: string
  payment_id: string
  order_id: string
  next_action_url?: string
  requires_action: boolean
}

// ── Razorpay Virtual Account (Smart Collect / bank transfer) ─────────────
// Maps to payment-service /v2/razorpay/vbank/*. Bank-transfer only for now
// (receiver_types: ['bank_account']). /vbank/create is an ENSURE endpoint: the
// backend persists the account's vbank_id, checks its live status with Razorpay,
// and returns the existing VA or provisions a replacement (see
// useEnsureActiveRazorpayVbank).

// A funding receiver on the VA. For bank_account we read account_number/ifsc/
// bank_name; vpa/qr fields are modelled for completeness but unused today.
export interface RazorpayVBankReceiver {
  id?: string
  entity: string // 'bank_account' | 'vpa' | 'qr_code'
  ifsc?: string
  bank_name?: string
  account_number?: string
  name?: string
  username?: string
  handle?: string
  address?: string
}

export interface RazorpayVBankCreatePayload {
  account_id: number
  receiver_types: string[] // ['bank_account']
  description?: string
  notes?: Record<string, string>
  // No amount_expected: Razorpay CLOSES a virtual account once that amount has
  // been credited, which would close it on the customer's first transfer and
  // force a new one on every recharge. The VA here is a permanent payee, so it
  // is provisioned open-ended and accepts any amount. payment-service ignores
  // the field if an older client still sends it.
}

// Response for create + get (POST /razorpay/vbank/create, GET /razorpay/vbank/:id).
export interface RRazorpayVBank {
  success: boolean
  uuid?: string
  message?: string
  vbank_id: string
  status: string // 'active' | 'closed'
  description?: string
  amount_paid?: number // rupees, present on get
  receivers: RazorpayVBankReceiver[]
  created_at?: number
  closed_at?: number
}

// A credit into the VA (GET /razorpay/vbank/:id/payments). status is 'captured'
// on a settled inbound transfer.
export interface RazorpayVBankPayment {
  payment_id: string
  amount: number // rupees
  currency: string
  method: string // 'upi' | 'netbanking' | 'bank_transfer'
  status: string // 'captured'
  vpa?: string
  bank?: string
  created_at?: number
}

export interface RRazorpayVBankPayments {
  success: boolean
  uuid?: string
  payments: RazorpayVBankPayment[]
}

export interface CashfreeVBank {
  virtual_account_id: string
  cf_virtual_account_id?: string
  account_id?: number
  virtual_account_number?: string
  virtual_ifsc?: string
  virtual_bank_name?: string
  expected_amount?: number
  received_amount?: number
  status: string
  created_at?: string
  expiry_date?: string
}

export interface CashfreeVBankCreatePayload {
  account_id: number
  expected_amount: number
  expiry_days: number
  product_type: 'plivo'
}

// Aligned with payment-service `VirtualAccountPaymentDetails`
// (`usecases/cashfree_vbank.go:67`). `payment_mode` is one of NEFT/RTGS/IMPS/UPI.
export interface CashfreeVBankPayment {
  cf_payment_id: number
  payment_amount: number
  payment_mode: string
  payment_status: string
  bank_reference?: string
  sender_name?: string
  sender_account_masked?: string
  payment_time?: string
  created_at?: string
}

export interface RCashfreeVBanksList {
  virtual_accounts: CashfreeVBank[]
  total_count: number
}

export interface RCashfreeVBankPayments {
  payments: CashfreeVBankPayment[]
  total_count: number
}

// Cashfree subscription statuses, verbatim from the API (UPPERCASE).
export type CashfreeSubscriptionStatus =
  | 'INITIALIZED'
  | 'ACTIVE'
  | 'ON_HOLD'
  | 'PAUSED'
  | 'CANCELLED'
  | 'COMPLETED'
  | 'BANK_APPROVAL_PENDING'

// Aligned with payment-service `CashfreeSubscriptionDetails`
// (`usecases/cashfree_subscription.go:60`). `card_brand`/`card_last_four`/
// `max_amount` are not yet emitted by the backend — the UI handles their
// absence with `??` fallbacks; they're kept here so they wire up automatically
// once the backend starts populating them.
export interface CashfreeSubscription {
  subscription_id: string
  cf_subscription_id?: string
  account_id: number
  plan_id: string
  amount: number
  currency?: 'INR' | 'USD'
  interval_type?: string
  status: CashfreeSubscriptionStatus
  card_last_four?: string
  card_brand?: string
  max_amount?: number
  created_at?: string
  next_charge_date?: string
}

export interface CashfreeSubscriptionCreatePayload {
  account_id: number
  plan_id: string
  amount: number
  product_type: 'plivo'
  return_url: string
  notify_url?: string
}

export interface RCashfreeSubscriptionCreate {
  success: boolean
  message?: string
  uuid?: string
  subscription_id: string
  cf_subscription_id?: string
  plan_id?: string
  status: CashfreeSubscriptionStatus
  amount?: number
  currency?: string
  // Cashfree subscription session ID — feed into the Cashfree JS SDK
  // (`cashfree.checkout({ paymentSessionId })`) to launch the auth page.
  session_id?: string
  first_charge_date?: string
}

export interface RCashfreeSubscriptionsList {
  subscriptions: CashfreeSubscription[]
  total_count: number
}
